import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProjectSchedule, scheduleCache as _scheduleCache } from '@/hooks/useProjectSchedule';
import { useScheduleSettings } from '@/hooks/useScheduleSettings';
import { useScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';
import { supabase } from '@/lib/supabase';
import { deduplicateChecklistItems } from '@/lib/checklistDedup';
import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  type GanttBarChange,
  type RecalculationResult,
  toISODate,
  parseISODate,
  isWorkingDay,
  addWorkingDays,
  calculateEndDate,
  nextWorkingDay,
  recalculateSchedule,
  parseScheduleDateInput,
  scheduleCalendarDayDelta,
  isScheduleItemIncluded,
} from '@/lib/scheduleUtils';
import { persistProjectSummary } from '@/lib/persistProjectSummary';
import { jphDebugLog } from '@/lib/jphDebug';
import { validateScheduleLinkMutation } from '@/lib/scheduleDependencyEngine';
import type { FormState, ScheduleItemModalResult } from '@/pages/management/projects/components/schedule/ScheduleItemModal';
import { defaultMainStageForManualScheduleItem } from '@/lib/scheduleStageMapping';

/**
 * Hook that provides all schedule‑tab state and actions.
 * It stitches together project schedule data, UI state (view mode, filters,
 * modals, etc.), and checklist‑progress integration.
 *
 * The implementation below is a lightweight, self‑contained version that
 * satisfies the UI components used throughout the project.  It can be
 * extended later without breaking existing behaviour.
 */
export function useScheduleTab(
  projectId: string,
  projectType?: string,
  canEdit: boolean = true,
  viewerRole: 'management' | 'subcontractor' | 'client' = 'management'
) {
  // ----------------------------------------------------------------------
  // Stage filters (used by the toolbar)
  // ----------------------------------------------------------------------
  const [mainStageFilter, setMainStageFilter] = useState('all');
  const [constructionSubStageFilter, setConstructionSubStageFilter] = useState('all');

  // ----------------------------------------------------------------------
  // Derived values — MUST be declared BEFORE any callback that references them
  // ----------------------------------------------------------------------
  const effectiveRole = viewerRole;
  const isManagement = viewerRole === 'management';
  const canEditReal = canEdit;

  // Settings & holidays first
  const {
    settings,
    holidays,
    projectHolidays,
    loading: settingsLoading,
    error: settingsError,
    reload: reloadSettings,
    reloadHolidays,
  } = useScheduleSettings(projectId);

  // Schedule items (depends on settings + holidays)
  const {
    items,
    links,
    loading: itemsLoading,
    error: itemsError,
    reload: reloadItems,
    criticalItemIds,
    computedRisk,
    healthSummary,
    includedItems,
    setItems,
    setLinks,
  } = useProjectSchedule(projectId, settings, holidays);

  // Notify other useStageScheduleChecklist instances to reload without mounting a duplicate hook.
  const reloadStages = useCallback(async () => {
    if (!projectId) return;
    try {
      window.dispatchEvent(
        new CustomEvent('__jphph_schedule_sync__', {
          detail: {
            projectId,
            isComplete: true,
            timestamp: new Date().toISOString(),
            source: 'useScheduleTab_reloadStages',
          },
        })
      );
    } catch { /* noop */ }
    await new Promise((r) => setTimeout(r, 100));
  }, [projectId]);

  const loading = itemsLoading || settingsLoading;
  const error = itemsError || settingsError;

  const effectiveSettings = useMemo(() => {
    if (settings) return settings;
    return {
      id: '',
      project_id: projectId || '',
      working_days: [1, 2, 3, 4, 5, 6],
      sunday_off: true,
      saturday_off: true,
      exclude_public_holidays: true,
      holiday_country: 'NZ',
      holiday_region: null,
      created_at: '',
      updated_at: '',
    };
  }, [settings, projectId]);

  const allHolidays = useMemo(
    () => [...holidays, ...projectHolidays],
    [holidays, projectHolidays]
  );

  // ----------------------------------------------------------------------
  // Checklist‑progress integration (must be before reload callback)
  // ----------------------------------------------------------------------
  const {
    progressMap: checklistProgressMap,
    reload: reloadChecklistProgress,
    linkedChecklists,
  } = useScheduleChecklistProgress(projectId);
  const [completionBlockItem, setCompletionBlockItem] = useState<ScheduleItem | null>(null);
  const onOpenCompletionBlock = (item: ScheduleItem) => setCompletionBlockItem(item);

  const reload = useCallback(() => {
    reloadSettings();
    reloadItems();
    reloadChecklistProgress();
  }, [reloadSettings, reloadItems, reloadChecklistProgress]);

  const { user, profile } = useAuth();
  const [viewMode, setViewMode] = useState<'list' | 'gantt'>('list');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [showAllLinks, setShowAllLinks] = useState(false);

  // NEW: pending schedule filter for UX clarity — persisted to localStorage per project
  const [pendingFilter, setPendingFilterState] = useState<'all' | 'pending_final' | 'final_100' | 'no_mapping' | 'incomplete_checklist'>(() => {
    try {
      const saved = localStorage.getItem(`jph_schedule_status_filter_${projectId}`);
      if (saved && ['all', 'pending_final', 'final_100', 'no_mapping', 'incomplete_checklist'].includes(saved)) {
        return saved as 'all' | 'pending_final' | 'final_100' | 'no_mapping' | 'incomplete_checklist';
      }
    } catch { /* ignore */ }
    return 'all';
  });

  const setPendingFilter = useCallback((v: 'all' | 'pending_final' | 'final_100' | 'no_mapping' | 'incomplete_checklist') => {
    setPendingFilterState(v);
    try {
      if (v === 'all') {
        localStorage.removeItem(`jph_schedule_status_filter_${projectId}`);
      } else {
        localStorage.setItem(`jph_schedule_status_filter_${projectId}`, v);
      }
    } catch { /* ignore */ }
  }, [projectId]);

  // NEW: bulk selection state
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [bulkMarking, setBulkMarking] = useState(false);

  // ----------------------------------------------------------------------
  // Scale & grouping controls
  // ----------------------------------------------------------------------
  const [ganttScale, setGanttScale] = useState<'day' | 'week' | 'month' | 'year'>('week');
  const [showPhases, setShowPhases] = useState(false);
  const [criticalPathEnabled, setCriticalPathEnabled] = useState(false);
  const [showCriticalPathOnly, setShowCriticalPathOnly] = useState(false);

  // ----------------------------------------------------------------------
  // Filter drawer state
  // ----------------------------------------------------------------------
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [filterTitleSearch, setFilterTitleSearch] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [filterCustomStart, setFilterCustomStart] = useState('');
  const [filterCustomEnd, setFilterCustomEnd] = useState('');

  // ----------------------------------------------------------------------
  // Modal state
  // ----------------------------------------------------------------------
  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem, setEditItem] = useState<ScheduleItem | null>(null);
  const [ganttEdit, setGanttEdit] = useState<GanttBarChange | null>(null);
  const [scheduleUndoSnapshot, setScheduleUndoSnapshot] = useState<
    Map<string, { start_date: string; end_date: string; duration_days?: number }> | null
  >(null);
  const [createDefaults, setCreateDefaults] = useState<Partial<FormState> | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // ----------------------------------------------------------------------
  // Save / error handling state
  // ----------------------------------------------------------------------
  const [saving, setSaving] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [notificationWarning, setNotificationWarning] = useState<string | null>(null);
  const clearMessages = () => {
    setSaveError(null);
    setSaveSuccess(null);
    setNotificationWarning(null);
  };
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [pendingRecalc, setPendingRecalc] = useState<{
    triggerItemIds: string[];
    affectedCompletedIds: string[];
    updatedItemMap: Map<string, { start_date: string; end_date: string; duration_days: number }>;
    changeDescriptions: string[];
    savedItemId?: string;
    afterMessage?: string;
  } | null>(null);
  const [settingsForm, setSettingsForm] = useState<any>(null);
  const [savingProjHoliday, setSavingProjHoliday] = useState(false);

  // ----------------------------------------------------------------------
  // Completion filter (declared after filter state so useEffect can reference it)
  // ----------------------------------------------------------------------
  const [completionFilter, setCompletionFilter] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [showExcluded, setShowExcluded] = useState(true);

  // Clear selection when filters change — MUST be after all dependency declarations
  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [pendingFilter, completionFilter, filterTitleSearch, filterAssignedTo, filterStatus, filterPhase, mainStageFilter, constructionSubStageFilter]);

  // ----------------------------------------------------------------------
  // Setup mode detection
  // ----------------------------------------------------------------------
  const isSetupMode = useMemo(() => {
    if (items.length === 0) return false;
    const included = items.filter((i) => i.is_included);
    if (included.length === 0) return false;
    const zeroProgress = included.filter((i) => i.progress === 0 && !i.is_complete).length;
    const complete = included.filter((i) => i.is_complete).length;
    return (zeroProgress / included.length > 0.5) && (complete / included.length < 0.1);
  }, [items]);

  const reloadHolidaysLocal = useCallback(() => {
    reloadHolidays();
  }, [reloadHolidays]);

  // ----------------------------------------------------------------------
  // Action placeholders (real implementations live in other hooks)
  // ----------------------------------------------------------------------
  const reloadSettingsLocal = useCallback(() => {
    // placeholder – real implementation inside useScheduleSettings
  }, []);

  const reloadHolidaysLocal2 = useCallback(() => {
    // placeholder – real implementation inside useProjectSchedule
  }, []);

  /**
   * Check if an item has successor links (used for recalc confirmation).
   * MUST be declared before handleSaveItem which references it.
   */
  const hasSuccessors = useCallback((itemId: string) => {
    return links.some((l) => l.predecessor_item_id === itemId);
  }, [links]);

  const clearCreateDefaults = useCallback(() => {
    setCreateDefaults(null);
  }, []);

  const applyManualScheduleStageDefault = useCallback((defaults: Partial<FormState>): Partial<FormState> => {
    if (defaults.main_stage) return defaults;
    const defaultStage = defaultMainStageForManualScheduleItem(projectType);
    return defaultStage ? { ...defaults, main_stage: defaultStage } : defaults;
  }, [projectType]);

  const openCreateItem = useCallback(() => {
    setCreateDefaults(applyManualScheduleStageDefault({}));
    setEditItem(null);
    setShowItemModal(true);
    clearMessages();
  }, [applyManualScheduleStageDefault, clearMessages]);

  /**
   * Persist downstream date updates from recalculateSchedule to Supabase and local state.
   */
  const applyDownstreamRecalculation = useCallback(async (
    recalcResult: RecalculationResult,
    options: {
      excludeItemId?: string;
      now?: string;
      successPrefix?: string;
    } = {}
  ): Promise<{ updatedCount: number; hadErrors: boolean }> => {
    const now = options.now ?? new Date().toISOString();

    if (recalcResult.circularWarning) {
      console.warn('Schedule recalculation circular warning:', recalcResult.circularWarning);
      setNotificationWarning(recalcResult.circularWarning);
    }

    if (recalcResult.blockedMoves?.length) {
      setNotificationWarning(
        `Some linked tasks were not moved: ${recalcResult.blockedMoves.slice(0, 2).join('; ')}${recalcResult.blockedMoves.length > 2 ? '…' : ''}`
      );
    }

    const downstreamUpdates = Array.from(recalcResult.updatedItems.entries())
      .filter(([id]) => id !== options.excludeItemId)
      .map(([id, dates]) => ({
        id,
        start_date: dates.start_date,
        end_date: dates.end_date,
        updated_at: now,
      }));

    if (downstreamUpdates.length === 0) {
      return { updatedCount: 0, hadErrors: false };
    }

    jphDebugLog('Persist downstream recalculation', {
      excludeItemId: options.excludeItemId,
      affectedCount: downstreamUpdates.length,
      affectedCompleted: recalcResult.affectedCompletedItems.length,
    });

    const CHUNK = 50;
    const errors: string[] = [];
    for (let chunkStart = 0; chunkStart < downstreamUpdates.length; chunkStart += CHUNK) {
      const chunk = downstreamUpdates.slice(chunkStart, chunkStart + CHUNK);
      const results = await Promise.all(
        chunk.map((u) =>
          supabase
            .from('jphph_project_schedule_items')
            .update({
              start_date: u.start_date,
              end_date: u.end_date,
              updated_at: u.updated_at,
            })
            .eq('id', u.id)
        )
      );
      results.forEach((r, idx) => {
        if (r.error) errors.push(`${downstreamUpdates[chunkStart + idx]?.id}: ${r.error.message}`);
      });
    }

    if (errors.length > 0) {
      console.error('Downstream update errors:', errors);
      setNotificationWarning(`Schedule recalculation failed for ${errors.length} item(s): ${errors[0]}`);
      return { updatedCount: 0, hadErrors: true };
    }

    const downstreamMap = new Map(downstreamUpdates.map((u) => [u.id, u]));
    setItems((prev) =>
      prev.map((i) => {
        const upd = downstreamMap.get(i.id);
        if (!upd) return i;
        return { ...i, start_date: upd.start_date, end_date: upd.end_date };
      })
    );

    const completedSuffix = recalcResult.affectedCompletedItems.length > 0
      ? ` (${recalcResult.affectedCompletedItems.length} completed item(s) not moved)`
      : '';

    if (options.successPrefix) {
      setSaveSuccess(`${options.successPrefix} ${downstreamUpdates.length} linked item(s) recalculated.${completedSuffix}`);
    }

    if (recalcResult.affectedCompletedItems.length > 0) {
      setNotificationWarning(`${recalcResult.affectedCompletedItems.length} completed/actual schedule item(s) were not automatically moved. Review them manually.`);
    }

    return { updatedCount: downstreamUpdates.length, hadErrors: false };
  }, [setItems]);

  const openCreateItemAtDate = useCallback((startDate: string) => {
    const end = calculateEndDate(startDate, 1, effectiveSettings, allHolidays);
    const defaults: Partial<FormState> = {
      start_date: startDate,
      end_date: end,
      duration_days: 1,
    };
    if (mainStageFilter !== 'all') defaults.main_stage = mainStageFilter;
    if (constructionSubStageFilter !== 'all') defaults.construction_sub_stage = constructionSubStageFilter;
    if (filterPhase !== 'all') defaults.phase = filterPhase;
    setCreateDefaults(applyManualScheduleStageDefault(defaults));
    setEditItem(null);
    setShowItemModal(true);
    clearMessages();
  }, [
    effectiveSettings,
    allHolidays,
    mainStageFilter,
    constructionSubStageFilter,
    filterPhase,
    applyManualScheduleStageDefault,
    clearMessages,
  ]);

  const handleGanttBarChange = useCallback((change: GanttBarChange) => {
    setGanttEdit(change);
    clearMessages();
  }, []);

  const handleIncludeAll = useCallback(async () => {
    if (!projectId || items.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    const excludedIds = items.filter((i) => i.is_included === false).map((i) => i.id);
    console.log('[useScheduleTab.handleIncludeAll] start', {
      projectId,
      table: 'jphph_project_schedule_items',
      totalItems: items.length,
      excludedIds: excludedIds.length,
    });

    try {
      const now = new Date().toISOString();
      const { error: err } = await supabase
        .from('jphph_project_schedule_items')
        .update({ is_included: true, updated_at: now })
        .eq('project_id', projectId)
        .eq('is_deleted', false);

      if (err) {
        console.error('[useScheduleTab.handleIncludeAll] update failed', {
          projectId,
          table: 'jphph_project_schedule_items',
          error: err,
        });
        setSaveError(`Failed to include all: ${err.message}`);
        return;
      }

      // Optimistic UI — do not rely on .select() after update (RLS may block returning rows).
      setItems((prev) => prev.map((i) => ({ ...i, is_included: true })));
      _scheduleCache.delete(projectId);

      const { data: verifyRows, error: verifyErr } = await supabase
        .from('jphph_project_schedule_items')
        .select('id, is_included')
        .eq('project_id', projectId)
        .eq('is_deleted', false);

      if (verifyErr) {
        console.error('[useScheduleTab.handleIncludeAll] verify read failed', {
          projectId,
          error: verifyErr,
        });
        setSaveSuccess('Schedule items included in view. Reload to confirm persistence.');
      } else {
        const includedAfter = (verifyRows ?? []).filter((r) => r.is_included !== false).length;
        const excludedAfter = (verifyRows ?? []).filter((r) => r.is_included === false).length;
        console.log('[useScheduleTab.handleIncludeAll] verify result', {
          projectId,
          total: verifyRows?.length ?? 0,
          includedAfter,
          excludedAfter,
        });
        if (excludedAfter > 0 && includedAfter === 0) {
          setSaveError(
            'Include all did not persist — database rows may still be excluded. Check permissions or run the recovery SQL.'
          );
        } else {
          setSaveSuccess(`Included ${includedAfter || items.length} schedule items in the Gantt view.`);
        }
      }

      await reloadItems();
    } catch (err: unknown) {
      console.error('[useScheduleTab.handleIncludeAll] unexpected error', { projectId, error: err });
      setSaveError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, items, setItems, reloadItems]);

  const handleExcludeAll = useCallback(async () => {
    if (!projectId || items.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const { error: err } = await supabase
        .from('jphph_project_schedule_items')
        .update({ is_included: false, updated_at: now })
        .eq('project_id', projectId)
        .eq('is_deleted', false);

      if (err) {
        setSaveError(`Failed to exclude all: ${err.message}`);
        return;
      }

      setItems((prev) => prev.map((i) => ({ ...i, is_included: false })));
      _scheduleCache.delete(projectId);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, items.length, setItems]);

  const handleToggleIncluded = useCallback(async (item: ScheduleItem, checked: boolean) => {
    if (!projectId || !item.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { error: err } = await supabase
        .from('jphph_project_schedule_items')
        .update({ is_included: checked, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .eq('is_deleted', false);

      if (err) {
        setSaveError(`Failed to update: ${err.message}`);
        return;
      }

      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, is_included: checked } : i
        )
      );

      _scheduleCache.delete(projectId);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, setItems]);

  /**
   * Save schedule item (create or update).
   * This was previously a placeholder — now fully implemented.
   */
  const handleSaveItem = useCallback(async (result: ScheduleItemModalResult) => {
    if (!projectId) return false;

    const { form, changeReason, publishToClient, skipRecalcConfirm, pendingLink } = result;
    const item = editItem;

    // Validate
    if (!form.title.trim()) {
      setSaveError('Title is required');
      return false;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    const now = new Date().toISOString();

    // ── Normalize dates to strict YYYY-MM-DD ──
    const normalizeDate = (val: string): string | null => {
      if (!val || val.trim() === '') return null;
      const parsed = parseScheduleDateInput(val);
      if (parsed) return parsed;
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const nStart = normalizeDate(form.start_date);
    const nEnd = normalizeDate(form.end_date);
    const nBaselineStart = normalizeDate(form.baseline_start_date);
    const nBaselineEnd = normalizeDate(form.baseline_end_date);
    const nActualStart = normalizeDate(form.actual_start_date);
    const nActualEnd = normalizeDate(form.actual_end_date);

    // ── Auto-complete when progress reaches 100 ──
    const nProgress = Math.max(0, Math.min(100, Number(form.progress) || 0));
    const nIsComplete = nProgress >= 100 ? true : form.is_complete;

    const resolvedMainStage =
      form.main_stage || defaultMainStageForManualScheduleItem(projectType) || null;

    // Build update payload using ONLY columns that exist in the DB schema
    const updatePayload: Record<string, unknown> = {
      title: form.title.trim(),
      phase: form.phase || null,
      main_stage: resolvedMainStage,
      construction_sub_stage: form.construction_sub_stage || null,
      duration_days: Math.max(1, Number(form.duration_days) || 1),
      start_date: nStart,
      end_date: nEnd,
      progress: nProgress,
      is_complete: nIsComplete,
      is_included: form.is_included,
      assigned_to: form.assigned_to || null,
      assigned_role: form.assigned_role || null,
      notes: form.notes || null,
      is_critical: form.is_critical,
      baseline_start_date: nBaselineStart,
      baseline_end_date: nBaselineEnd,
      actual_start_date: nActualStart,
      actual_end_date: nActualEnd,
      delay_reason: form.delay_reason || null,
      delay_status: form.delay_status || 'on_track',
      schedule_risk_level: form.schedule_risk_level || 'normal',
      bar_color: form.bar_color || null,
      bar_color_mode: form.bar_color_mode || 'phase',
      gantt_color: form.gantt_color || null,
      updated_at: now,
    };

    jphDebugLog('Edit schedule save clicked', {
      scheduleItemId: item?.id ?? null,
      isNewItem: !item,
      formValues: form,
      normalizedDates: { start: nStart, end: nEnd, baselineStart: nBaselineStart, baselineEnd: nBaselineEnd, actualStart: nActualStart, actualEnd: nActualEnd },
      autoComplete: nIsComplete !== form.is_complete,
    });

    try {
      let savedItemId: string;

      if (item) {
        // ── UPDATE existing item ──
        savedItemId = item.id;

        // Detect significant changes for change log
        const datesChanged =
          nStart !== (item.start_date || null) ||
          nEnd !== (item.end_date || null) ||
          Number(form.duration_days) !== item.duration_days;

        jphDebugLog('Edit schedule update payload', {
          itemId: savedItemId,
          updatePayload,
          datesChanged,
          skipRecalcConfirm,
          hasSuccessors: hasSuccessors(savedItemId),
        });

        const { data, error } = await supabase
          .from('jphph_project_schedule_items')
          .update(updatePayload)
          .eq('id', savedItemId)
          .eq('is_deleted', false)
          .select()
          .maybeSingle();

        jphDebugLog('Edit schedule update result', {
          data: data ? { id: data.id, title: data.title } : null,
          error: error ? error.message : null,
        });

        if (error) {
          setSaveError(`Failed to save: ${error.message}`);
          return false;
        }

        // ── CRITICAL: verify the update actually affected a row ──
        if (!data) {
          console.error('Edit schedule update returned null data — possible RLS block or item not found', {
            itemId: savedItemId,
            projectId,
            userId: user?.id,
            userType: profile?.user_type,
          });
          setSaveError('Update failed: item not found or access denied. Please refresh the page and try again.');
          return false;
        }

        // Write change log for significant changes
        if (datesChanged && changeReason) {
          try {
            await supabase.from('jphph_project_schedule_change_logs').insert({
              project_id: projectId,
              schedule_item_id: savedItemId,
              schedule_item_title: form.title.trim(),
              change_type: 'update',
              old_start_date: item.start_date || null,
              old_end_date: item.end_date || null,
              new_start_date: nStart,
              new_end_date: nEnd,
              old_duration_days: item.duration_days,
              new_duration_days: Math.max(1, Number(form.duration_days) || 1),
              affected_item_count: 1,
              changed_by: user?.email || user?.id || 'unknown',
              changed_by_role: profile?.user_type || 'management',
              change_reason: changeReason.trim(),
              is_client_visible: publishToClient || false,
            });
          } catch (logErr) {
            // Non-fatal: change log failure should not block the save
            console.error('Failed to write change log:', logErr);
          }
        }

        // Recalculate downstream items if dates/duration changed and has successors
        const datesOrDurationChanged =
          Number(form.duration_days) !== item.duration_days ||
          nStart !== (item.start_date || null) ||
          nEnd !== (item.end_date || null);

        if (!skipRecalcConfirm && datesOrDurationChanged && hasSuccessors(savedItemId)) {
          try {
            const updatedItem: ScheduleItem = {
              ...(data as ScheduleItem),
              id: savedItemId,
              project_id: item.project_id,
              start_date: nStart,
              end_date: nEnd,
              duration_days: Math.max(1, Number(form.duration_days) || 1),
            };

            const allItemsForRecalc = items.map((i) =>
              i.id === savedItemId ? updatedItem : i
            );

            jphDebugLog('Schedule dependency recalculation start', {
              projectId,
              changedItemId: savedItemId,
              changedFields: {
                duration_days: Number(form.duration_days),
                start_date: nStart,
                end_date: nEnd,
                action: 'itemSave',
              },
            });

            const sourceDeltaDays = item.start_date && nStart
              ? scheduleCalendarDayDelta(item.start_date, nStart)
              : 0;

            const recalcResult = recalculateSchedule(
              allItemsForRecalc,
              links,
              effectiveSettings,
              allHolidays,
              {
                triggerItemIds: [savedItemId],
                fixedItemIds: [savedItemId],
                sourceDeltaDays,
                skipCompletedItems: true,
                projectId,
                triggerSource: 'saveItem',
              }
            );

            if (recalcResult.circularWarning) {
              console.warn('Recalculate downstream: circular warning:', recalcResult.circularWarning);
            }

            await applyDownstreamRecalculation(recalcResult, {
              excludeItemId: savedItemId,
              now,
              successPrefix: 'Changes saved.',
            });
          } catch (recalcErr) {
            console.error('Recalculate downstream error:', recalcErr);
          }
        }

        // Optimistic local update
        setItems((prev) =>
          prev.map((i) =>
            i.id === savedItemId
              ? { ...i, ...updatePayload, id: savedItemId } as ScheduleItem
              : i
          )
        );
      } else {
        // ── CREATE new item ──
        // Find max sort_order for insertion at end
        const maxSort = items.length > 0
          ? Math.max(...items.map((i) => i.sort_order))
          : 0;

        const insertPayload = {
          ...updatePayload,
          project_id: projectId,
          sort_order: maxSort + 10,
          is_deleted: false,
          created_at: now,
        };

        jphDebugLog('Create schedule insert payload', insertPayload);

        const { data, error } = await supabase
          .from('jphph_project_schedule_items')
          .insert(insertPayload)
          .select()
          .maybeSingle();

        jphDebugLog('Create schedule insert result', {
          data: data ? { id: data.id, title: data.title } : null,
          error: error ? error.message : null,
        });

        if (error) {
          setSaveError(`Failed to create: ${error.message}`);
          return false;
        }

        if (!data) {
          console.error('Create schedule insert returned null data');
          setSaveError('Failed to create schedule item: no data returned.');
          return false;
        }

        savedItemId = data.id;
        setItems((prev) => [...prev, data as ScheduleItem]);

        if (pendingLink?.otherItemId) {
          const linkPayload = pendingLink.direction === 'predecessor'
            ? {
                predecessor_item_id: pendingLink.otherItemId,
                successor_item_id: savedItemId,
                link_type: pendingLink.link_type,
                lag_days: pendingLink.lag_days,
              }
            : {
                predecessor_item_id: savedItemId,
                successor_item_id: pendingLink.otherItemId,
                link_type: pendingLink.link_type,
                lag_days: pendingLink.lag_days,
              };
          try {
            await handleUpsertLink(null, linkPayload);
          } catch (linkErr) {
            const linkMsg = linkErr instanceof Error ? linkErr.message : 'Link creation failed';
            setNotificationWarning(
              `Schedule item created, but dependency link could not be saved: ${linkMsg}`
            );
          }
        }
      }

      // Invalidate caches
      _scheduleCache.delete(projectId);

      // Persist project summary
      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        console.error('Failed to persist project summary after save:', persistErr);
      }

      setSaveSuccess(item ? 'Changes saved successfully' : 'Schedule item created');
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      console.error('handleSaveItem error:', err);
      setSaveError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    projectId,
    projectType,
    editItem,
    items,
    links,
    effectiveSettings,
    allHolidays,
    user,
    profile,
    hasSuccessors,
    applyDownstreamRecalculation,
    setItems,
  ]);

  /**
   * Delete schedule item (soft delete + link cleanup).
   */
  const handleDelete = useCallback(async (itemId: string) => {
    if (!projectId || !itemId) return;

    jphDebugLog('Delete schedule item clicked', { itemId, projectId });

    setSaving(true);
    setSaveError(null);

    const now = new Date().toISOString();

    try {
      // 1. Soft delete the schedule item
      const { error: delErr } = await supabase
        .from('jphph_project_schedule_items')
        .update({
          is_deleted: true,
          deleted_at: now,
          deleted_by: user?.id || null,
          updated_at: now,
        })
        .eq('id', itemId)
        .eq('project_id', projectId);

      if (delErr) {
        console.error('Soft delete schedule item error:', delErr);
        setSaveError(`Failed to delete: ${delErr.message}`);
        return;
      }

      jphDebugLog('Soft delete schedule item success', { itemId });

      // 2. Clean up dependency links where this item is predecessor or successor
      const { error: linkErr } = await supabase
        .from('jphph_project_schedule_links')
        .delete()
        .or(`predecessor_item_id.eq.${itemId},successor_item_id.eq.${itemId}`);

      if (linkErr) {
        console.error('Delete related schedule links error:', linkErr);
        // Non-fatal: the item is already deleted, just log it
      } else {
        jphDebugLog('Deleted related schedule links', { itemId });
      }

      // 3. Remove from checklist mappings
      const { error: mapErr } = await supabase
        .from('jphph_checklist_schedule_item_mappings')
        .delete()
        .eq('project_schedule_item_id', itemId);

      if (mapErr) {
        console.error('Delete checklist mappings error:', mapErr);
      }

      // 4. Optimistic local update — remove deleted item from state
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      // Also remove any links involving this item
      setLinks((prev) =>
        prev.filter(
          (l) => l.predecessor_item_id !== itemId && l.successor_item_id !== itemId
        )
      );

      // 5. Invalidate cache
      _scheduleCache.delete(projectId);

      // 6. Persist project summary
      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        console.error('Failed to persist project summary after delete:', persistErr);
      }

      setSaveSuccess('Schedule item deleted');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      console.error('handleDelete error:', err);
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [projectId, user, setItems, setLinks]);

  /**
   * Keep the open modal item in sync when link recalculation moves its dates.
   */
  const syncEditItemDatesFromRecalc = useCallback((recalcResult: RecalculationResult) => {
    setEditItem((prev) => {
      if (!prev) return prev;
      const dates = recalcResult.updatedItems.get(prev.id);
      if (!dates) return prev;
      return { ...prev, start_date: dates.start_date, end_date: dates.end_date };
    });
  }, []);

  /**
   * Upsert schedule link (create or update dependency).
   */
  const handleUpsertLink = useCallback(async (
    oldLinkId: string | null,
    payload: { predecessor_item_id: string; successor_item_id: string; link_type: 'FS' | 'SS' | 'FF' | 'SF'; lag_days: number }
  ) => {
    if (!projectId) {
      throw new Error('Project not loaded');
    }
    setSavingLink(true);
    setSaveError(null);
    setSaveSuccess(null);

    const nLagDays = typeof payload.lag_days === 'number' && !Number.isNaN(payload.lag_days)
      ? payload.lag_days
      : Number(payload.lag_days) || 0;

    jphDebugLog('Update schedule link clicked', {
      linkId: oldLinkId,
      predecessorId: payload.predecessor_item_id,
      successorId: payload.successor_item_id,
      linkType: payload.link_type,
      lagDays: nLagDays,
    });

    const linkValidation = validateScheduleLinkMutation({
      predecessorId: payload.predecessor_item_id,
      successorId: payload.successor_item_id,
      links,
      editingLinkId: oldLinkId,
    });
    if (linkValidation.ok === false) {
      const validationError = linkValidation.error;
      setSaveError(validationError);
      setSavingLink(false);
      throw new Error(validationError);
    }

    try {
      if (oldLinkId) {
        const updatePayload = {
          predecessor_item_id: payload.predecessor_item_id,
          successor_item_id: payload.successor_item_id,
          link_type: payload.link_type,
          lag_days: nLagDays,
        };

        jphDebugLog('Update schedule link payload', updatePayload);

        // Use .select() to verify the row was actually updated
        const { data, error } = await supabase
          .from('jphph_project_schedule_links')
          .update(updatePayload)
          .eq('id', oldLinkId)
          .select()
          .maybeSingle();

        jphDebugLog('Update schedule link result', {
          data: data ? { id: data.id, lag_days: data.lag_days, link_type: data.link_type } : null,
          error: error ? error.message : null,
        });

        if (error) {
          console.error('Update schedule link Supabase error:', error);
          setSaveError(`Failed to update link: ${error.message}`);
          throw error;
        }

        if (!data) {
          const errMsg = 'Update failed: link not found or access denied. Please refresh and try again.';
          console.error('Update schedule link returned null data — possible RLS block or row not found', {
            oldLinkId,
            projectId,
            userId: user?.id,
            userType: profile?.user_type,
          });
          setSaveError(errMsg);
          throw new Error(errMsg);
        }
      } else {
        const insertPayload = {
          project_id: projectId,
          predecessor_item_id: payload.predecessor_item_id,
          successor_item_id: payload.successor_item_id,
          link_type: payload.link_type,
          lag_days: nLagDays,
        };

        jphDebugLog('Create schedule link payload', insertPayload);

        const { data, error } = await supabase
          .from('jphph_project_schedule_links')
          .insert(insertPayload)
          .select()
          .maybeSingle();

        jphDebugLog('Create schedule link result', {
          data: data ? { id: data.id, lag_days: data.lag_days, link_type: data.link_type } : null,
          error: error ? error.message : null,
        });

        if (error) {
          console.error('Create schedule link Supabase error:', error);
          setSaveError(`Failed to create link: ${error.message}`);
          throw error;
        }

        if (!data) {
          const errMsg = 'Failed to create link: no data returned.';
          console.error('Create schedule link returned null data');
          setSaveError(errMsg);
          throw new Error(errMsg);
        }
      }

      // Refresh links from Supabase
      const { data: linkRows } = await supabase
        .from('jphph_project_schedule_links')
        .select('*')
        .eq('project_id', projectId);

      const freshLinks = (linkRows || []) as ScheduleLink[];
      setLinks(freshLinks);

      // Invalidate schedule cache so reload() gets fresh data
      _scheduleCache.delete(projectId);

      const successorId = payload.successor_item_id;
      const predecessorId = payload.predecessor_item_id;
      const triggerSource = oldLinkId ? 'updateLink' : 'createLink';

      jphDebugLog('Schedule dependency recalculation start', {
        projectId,
        changedItemId: successorId,
        changedFields: {
          link_type: payload.link_type,
          lag_days: nLagDays,
          action: triggerSource,
        },
      });

      const recalcResult = recalculateSchedule(items, freshLinks, effectiveSettings, allHolidays, {
        triggerItemIds: [successorId],
        fixedItemIds: [predecessorId],
        skipCompletedItems: true,
        projectId,
        triggerSource,
      });

      if (recalcResult.circularWarning) {
        console.warn('handleUpsertLink recalc: circular warning:', recalcResult.circularWarning);
        setSaveError(`Link saved, but: ${recalcResult.circularWarning}`);
      }

      const linkVerb = oldLinkId ? 'updated' : 'created';
      const { updatedCount, hadErrors } = await applyDownstreamRecalculation(recalcResult, {
        successPrefix: `Link ${linkVerb}.`,
      });

      if (hadErrors) {
        throw new Error('Schedule recalculation failed');
      }

      syncEditItemDatesFromRecalc(recalcResult);

      if (updatedCount === 0 && !recalcResult.circularWarning) {
        setSaveSuccess(`Link ${linkVerb}. No downstream items affected.`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Link update failed';
      console.error('handleUpsertLink error:', err);
      setSaveError(msg);
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setSavingLink(false);
    }
  }, [projectId, user, profile, links, items, setLinks, setItems, effectiveSettings, allHolidays, applyDownstreamRecalculation, syncEditItemDatesFromRecalc]);

  /**
   * Remove schedule link and recalculate downstream items.
   */
  const handleRemoveLink = useCallback(async (linkId: string) => {
    if (!projectId || !linkId) return;
    setDeletingLinkId(linkId);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from('jphph_project_schedule_links')
        .delete()
        .eq('id', linkId);
      if (error) throw error;

      const freshLinks = links.filter((l) => l.id !== linkId);
      setLinks(freshLinks);

      const removedLink = links.find((l) => l.id === linkId);
      const successorId = removedLink?.successor_item_id;
      const predecessorId = removedLink?.predecessor_item_id;

      if (successorId) {
        jphDebugLog('Schedule dependency recalculation start', {
          projectId,
          changedItemId: successorId,
          changedFields: { action: 'removeLink', removedLinkId: linkId },
        });

        const recalcResult = recalculateSchedule(items, freshLinks, effectiveSettings, allHolidays, {
          triggerItemIds: [successorId],
          fixedItemIds: predecessorId ? [predecessorId] : [],
          skipCompletedItems: true,
          projectId,
          triggerSource: 'removeLink',
        });

        if (recalcResult.circularWarning) {
          console.warn('handleRemoveLink recalc warning:', recalcResult.circularWarning);
        }

        await applyDownstreamRecalculation(recalcResult, { successPrefix: 'Link removed.' });
        syncEditItemDatesFromRecalc(recalcResult);
      }

      _scheduleCache.delete(projectId);
      if (!successorId) setSaveSuccess('Link removed');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Link removal failed');
    } finally {
      setDeletingLinkId(null);
    }
  }, [projectId, links, items, effectiveSettings, allHolidays, setLinks, applyDownstreamRecalculation, syncEditItemDatesFromRecalc]);

  /**
   * Recalculate schedule dates from dependency links.
   */
  const handleRecalc = useCallback(async () => {
    if (!projectId || items.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      jphDebugLog('Schedule dependency recalculation start', {
        projectId,
        changedItemId: null,
        changedFields: { action: 'manualFullRecalc' },
      });

      // Full recalculation — no trigger restriction (all items)
      const recalcResult = recalculateSchedule(
        items,
        links,
        effectiveSettings,
        allHolidays,
        {
          triggerItemIds: [],
          skipCompletedItems: true,
          projectId,
          triggerSource: 'manualRecalc',
        }
      );
      const now = new Date().toISOString();

      if (recalcResult.circularWarning) {
        setSaveError(`Recalculation failed: ${recalcResult.circularWarning}`);
        setSaving(false);
        return;
      }

      const updates = Array.from(recalcResult.updatedItems.entries())
        .map(([id, dates]) => ({
          id,
          start_date: dates.start_date,
          end_date: dates.end_date,
          updated_at: now,
        }));

      if (updates.length === 0) {
        const skippedMsg = recalcResult.affectedCompletedItems.length > 0
          ? ` (${recalcResult.affectedCompletedItems.length} completed item(s) not moved)`
          : '';
        setSaveSuccess(`No schedule changes needed.${skippedMsg}`);
        setSaving(false);
        return;
      }

      const CHUNK = 50;
      const updateErrors: string[] = [];
      for (let i = 0; i < updates.length; i += CHUNK) {
        const chunk = updates.slice(i, i + CHUNK);
        const results = await Promise.all(
          chunk.map((u) =>
            supabase
              .from('jphph_project_schedule_items')
              .update({
                start_date: u.start_date,
                end_date: u.end_date,
                updated_at: u.updated_at,
              })
              .eq('id', u.id)
          )
        );
        results.forEach((r, idx) => {
          if (r.error) updateErrors.push(`${updates[i + idx]?.id}: ${r.error.message}`);
        });
      }

      if (updateErrors.length > 0) {
        console.error('handleRecalc update errors:', updateErrors);
        setSaveError(`Schedule recalculation failed for ${updateErrors.length} item(s): ${updateErrors[0]}`);
        setSaving(false);
        return;
      }

      // Apply to local state immediately (Gantt/List refresh without page reload)
      const updatesMap = new Map(updates.map((u) => [u.id, u]));
      setItems((prev) =>
        prev.map((i) => {
          const upd = updatesMap.get(i.id);
          if (!upd) return i;
          return { ...i, start_date: upd.start_date, end_date: upd.end_date };
        })
      );

      _scheduleCache.delete(projectId);

      const completedSkipped = recalcResult.affectedCompletedItems.length;
      const completedNote = completedSkipped > 0
        ? ` ${completedSkipped} completed item(s) not moved.`
        : '';
      setSaveSuccess(`Schedule recalculated. ${updates.length} linked item(s) updated.${completedNote}`);

      if (completedSkipped > 0) {
        setNotificationWarning(`${completedSkipped} completed/actual item(s) were not moved. Review them manually if needed.`);
      }
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Recalculation failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, items, links, effectiveSettings, allHolidays, setItems]);

  /**
   * Save baseline dates for all current items.
   */
  const handleSaveBaseline = useCallback(async () => {
    if (!projectId || items.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const included = items.filter((i) => i.is_included && !i.is_deleted);

      const CHUNK = 50;
      for (let i = 0; i < included.length; i += CHUNK) {
        const chunk = included.slice(i, i + CHUNK);
        await Promise.all(
          chunk.map((item) =>
            supabase
              .from('jphph_project_schedule_items')
              .update({
                baseline_start_date: item.start_date,
                baseline_end_date: item.end_date,
                updated_at: now,
              })
              .eq('id', item.id)
          )
        );
      }

      _scheduleCache.delete(projectId);
      reloadItems();
      setSaveSuccess('Baseline saved for all schedule items');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Baseline save failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, items, reloadItems]);

  /**
   * Save schedule settings.
   */
  const handleSaveSettings = useCallback(async () => {
    if (!projectId || !settingsForm) return;
    setSaving(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('jphph_project_schedule_settings')
        .upsert({
          project_id: projectId,
          ...settingsForm,
          updated_at: now,
        }, { onConflict: 'project_id' });

      if (error) throw error;

      reloadSettings();
      setSaveSuccess('Schedule settings saved');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Settings save failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, settingsForm, reloadSettings]);

  /**
   * Add project-specific holiday.
   */
  const handleAddProjectHoliday = useCallback(async (holiday: { date: string; name: string }) => {
    if (!projectId) return;
    setSavingProjHoliday(true);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from('jphph_project_schedule_holidays')
        .insert({
          project_id: projectId,
          holiday_date: holiday.date,
          holiday_name: holiday.name,
          is_active: true,
        });

      if (error) throw error;
      await reloadHolidays();
      setSaveSuccess('Holiday added');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Holiday add failed');
    } finally {
      setSavingProjHoliday(false);
    }
  }, [projectId, reloadHolidays]);

  /**
   * Remove project-specific holiday.
   */
  const handleRemoveProjectHoliday = useCallback(async (holidayId: string) => {
    if (!projectId || !holidayId) return;
    setSavingProjHoliday(true);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from('jphph_project_schedule_holidays')
        .delete()
        .eq('id', holidayId)
        .eq('project_id', projectId);

      if (error) throw error;
      await reloadHolidays();
      setSaveSuccess('Holiday removed');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Holiday removal failed');
    } finally {
      setSavingProjHoliday(false);
    }
  }, [projectId, reloadHolidays]);

  /**
   * Setup default schedule from template.
   */
  const [scheduleSetupState, setScheduleSetupState] = useState<'idle' | 'loading' | 'success' | 'error' | 'exists'>('idle');
  const [scheduleSetupMessage, setScheduleSetupMessage] = useState('');

  const handleSetupSchedule = useCallback(async () => {
    if (!projectId) return;
    if (projectType === 'Renovation' || projectType === 'Other') {
      setSaveError('Add schedule tasks manually using Add Task.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    setScheduleSetupState('loading');
    setScheduleSetupMessage('');
    try {
      const { setupScheduleForProject } = await import('@/lib/projectScheduleSetup');
      const result = await setupScheduleForProject(projectId);
      if (result.alreadyExists) {
        setScheduleSetupState('exists');
        setScheduleSetupMessage(result.message);
        return;
      }
      if (!result.success || result.itemsCreated === 0) {
        const errMsg = result.warning || result.message;
        setScheduleSetupState('error');
        setScheduleSetupMessage(errMsg);
        setSaveError(errMsg);
        return;
      }
      _scheduleCache.delete(projectId);
      reloadItems();
      setScheduleSetupState('success');
      setScheduleSetupMessage(result.message);
      setSaveSuccess(result.message);
      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        console.error('Failed to persist project summary after schedule setup:', persistErr);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Schedule setup failed';
      setScheduleSetupState('error');
      setScheduleSetupMessage(errMsg);
      setSaveError(errMsg);
    } finally {
      setSaving(false);
    }
  }, [projectId, projectType, reloadItems]);

  /**
   * Toggle schedule item completion.
   * When checklist items are linked and NOT all required are complete,
   * the UI (ListView/GanttView) should call onOpenCompletionBlock first.
   */
  const handleToggleComplete = useCallback(async (item: ScheduleItem, checked: boolean) => {
    if (!projectId || !item.id) return;

    const cp = checklistProgressMap.get(item.id);
    const hasLinked = cp?.hasLinkedItems ?? false;

    const beforeItem = {
      id: item.id,
      title: item.title,
      is_complete: item.is_complete,
      progress: item.progress,
      actual_end_date: item.actual_end_date,
      hasLinkedChecklist: hasLinked,
      checklistProgress: cp?.progressPct,
      checklistAllComplete: cp?.allRequiredComplete,
    };

    setSaving(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const today = now.split('T')[0];

      const update: Record<string, unknown> = {
        is_complete: checked,
        progress: checked ? 100 : (hasLinked ? (cp?.progressPct ?? 0) : 0),
        actual_end_date: checked ? today : null,
        updated_at: now,
      };

      if (checked && !item.actual_start_date && item.start_date) {
        update.actual_start_date = item.start_date;
      } else if (!checked) {
        update.actual_start_date = null;
      }

      // eslint-disable-next-line no-console
      jphDebugLog('Mark schedule item complete', { itemId: item.id, beforeItem, updatePayload: { ...update }, updateError: null, afterItem: null });

      const { error: err } = await supabase
        .from('jphph_project_schedule_items')
        .update(update)
        .eq('id', item.id);

      if (err) {
        // eslint-disable-next-line no-console
        jphDebugLog('Mark schedule item complete', { itemId: item.id, beforeItem, updatePayload: { ...update }, updateError: err.message, afterItem: null });
        setSaveError(`Failed to update: ${err.message}`);
        return;
      }

      const afterItem = { id: item.id, is_complete: checked, progress: checked ? 100 : (hasLinked ? (cp?.progressPct ?? 0) : 0), actual_end_date: checked ? today : null, updated_at: now };
      // eslint-disable-next-line no-console
      jphDebugLog('Mark schedule item complete', { itemId: item.id, beforeItem, updatePayload: { ...update }, updateError: null, afterItem });

      setItems((prev) => prev.map((i) =>
        i.id === item.id
          ? { ...i, is_complete: checked, progress: checked ? 100 : (hasLinked ? (cp?.progressPct ?? 0) : 0), actual_end_date: checked ? today : null, actual_start_date: checked ? (item.actual_start_date || item.start_date || null) : null, updated_at: now }
          : i
      ));

      _scheduleCache.delete(projectId);

      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        // eslint-disable-next-line no-console
        console.error('Failed to persist project summary after toggle:', persistErr);
      }

      try {
        window.dispatchEvent(new CustomEvent('__jphph_schedule_sync__', { detail: { projectId, scheduleItemId: item.id, isComplete: checked, timestamp: now } }));
      } catch { /* noop */ }

      await reloadStages();
      setSaveSuccess(checked ? 'Item marked complete' : 'Item marked incomplete');

      const updatedItems = items.map((i) =>
        i.id === item.id
          ? { ...i, is_complete: checked, progress: checked ? 100 : (hasLinked ? (cp?.progressPct ?? 0) : 0), actual_end_date: checked ? today : null }
          : i
      );
      const totalScheduleItems = updatedItems.filter((i) => i.is_included !== false).length;
      const completedScheduleItems = updatedItems.filter((i) => i.is_complete || i.progress >= 100 || i.actual_end_date).length;
      const pendingScheduleItems = totalScheduleItems - completedScheduleItems;
      const newScheduleFinalProgress = totalScheduleItems > 0 ? Math.round((completedScheduleItems / totalScheduleItems) * 100) : 0;
      let totalLinked = 0; let totalPct = 0;
      for (const it of updatedItems) { const c = checklistProgressMap.get(it.id); if (c?.hasLinkedItems) { totalLinked += 1; totalPct += c.progressPct; } }
      const newChecklistFinalProgress = totalLinked > 0 ? Math.round(totalPct / totalLinked) : 0;
      const newProjectProgress = Math.round(newScheduleFinalProgress * 0.5 + newChecklistFinalProgress * 0.5);
      // eslint-disable-next-line no-console
      jphDebugLog('SCHEDULE TAB DISPLAY DATA', { displayedProjectProgress: newProjectProgress, displayedScheduleProgress: newScheduleFinalProgress, displayedChecklistProgress: newChecklistFinalProgress, displayedPendingScheduleItems: pendingScheduleItems, source: 'ScheduleTab' });
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      jphDebugLog('Mark schedule item complete', { itemId: item.id, beforeItem, updatePayload: null, updateError: err instanceof Error ? err.message : 'Unknown error', afterItem: null });
      setSaveError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, checklistProgressMap, reloadStages, items]);

  /**
   * Progress slider change — blocked when linked checklist exists.
   */
  const handleProgressChange = useCallback(async (itemId: string, progress: number) => {
    if (!projectId || !itemId) return;
    const cp = checklistProgressMap.get(itemId);
    if (cp?.hasLinkedItems) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { error: err } = await supabase
        .from('jphph_project_schedule_items')
        .update({ progress: Math.max(0, Math.min(100, progress)), is_complete: progress >= 100, updated_at: new Date().toISOString() })
        .eq('id', itemId);
      if (err) { setSaveError(`Failed to update: ${err.message}`); } else { reloadItems(); }
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, checklistProgressMap, reloadItems]);

  /**
   * Recalculate all schedule item progress from linked checklists.
   */
  const handleRecalculateProgress = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data: ci } = await supabase.from('jphph_checklist_items').select('id, title, is_completed, management_checked, subcontractor_checked, requires_management_check, requires_subcontractor_check, source_template_item_id').eq('project_id', projectId);
      // CRITICAL FIX: Deduplicate using shared utility (handles NULL template IDs)
      const rawCi = (ci || []) as Array<{ id: string; title: string; is_completed: boolean; management_checked: boolean; subcontractor_checked: boolean; requires_management_check: boolean; requires_subcontractor_check: boolean; source_template_item_id: string | null; checklist_id?: string | null }>;
      const { unique: dedupedCi } = deduplicateChecklistItems(rawCi);
      const checklistMap = new Map(dedupedCi.map((c) => [c.id, c]));
      const { data: mappings } = await supabase.from('jphph_checklist_schedule_item_mappings').select('project_schedule_item_id, project_checklist_item_id').eq('project_id', projectId);
      const scheduleToChecklist = new Map<string, Array<{ id: string; management_checked: boolean; subcontractor_checked: boolean; requires_management_check: boolean; requires_subcontractor_check: boolean }>>();
      for (const m of (mappings || []) as Array<{ project_schedule_item_id: string; project_checklist_item_id: string }>) {
        const ci2 = checklistMap.get(m.project_checklist_item_id);
        if (!ci2) continue;
        const arr = scheduleToChecklist.get(m.project_schedule_item_id) || [];
        arr.push(ci2);
        scheduleToChecklist.set(m.project_schedule_item_id, arr);
      }
      const now = new Date().toISOString();
      const { data: currentScheduleItems } = await supabase.from('jphph_project_schedule_items').select('id, is_complete').eq('project_id', projectId);
      const currentIsCompleteMap = new Map((currentScheduleItems || []).map((si: { id: string; is_complete: boolean }) => [si.id, si.is_complete]));
      const updates: Array<{ id: string; progress: number; is_complete: boolean }> = [];
      for (const [scheduleId, checklists] of scheduleToChecklist) {
        let totalRequired = 0; let completedRequired = 0;
        for (const c of checklists) {
          if (c.requires_management_check !== false) totalRequired += 1;
          if (c.requires_subcontractor_check === true) totalRequired += 1;
          if (c.requires_management_check !== false && c.management_checked) completedRequired += 1;
          if (c.requires_subcontractor_check === true && c.subcontractor_checked) completedRequired += 1;
        }
        const progress = totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0;
        const checklistComplete = checklists.every((c) => { const mgmtOk = c.requires_management_check === false || c.management_checked; const subOk = c.requires_subcontractor_check !== true || c.subcontractor_checked; return mgmtOk && subOk; });
        const isManuallyComplete = currentIsCompleteMap.get(scheduleId) ?? false;
        const isComplete = isManuallyComplete || checklistComplete;
        updates.push({ id: scheduleId, progress, is_complete: isComplete });
      }
      const CHUNK = 50;
      for (let i = 0; i < updates.length; i += CHUNK) {
        const chunk = updates.slice(i, i + CHUNK);
        await Promise.all(chunk.map((u) => supabase.from('jphph_project_schedule_items').update({ progress: u.progress, is_complete: u.is_complete, updated_at: now }).eq('id', u.id)));
      }
      _scheduleCache.delete(projectId);
      reload();
      try { await persistProjectSummary(projectId); } catch (persistErr) { console.error('Failed to persist project summary after recalculate:', persistErr); }
      setSaveSuccess(updates.length > 0 ? `Recalculated ${updates.length} schedule items` : 'No mapped schedule items found — nothing to recalculate');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to recalculate progress');
    } finally {
      setSaving(false);
    }
  }, [projectId, reload]);

  const handleRecalcCriticalPath = useCallback(() => { setShowRecalcConfirm(true); }, []);

  const confirmRecalcCriticalPath = useCallback(async () => {
    if (!projectId || items.length === 0) { setShowRecalcConfirm(false); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const { calculateCriticalPath } = await import('@/lib/scheduleUtils');
      const cpResult = calculateCriticalPath(items, links, effectiveSettings, allHolidays);
      const criticalIds = Array.from(cpResult.criticalItemIds);
      const nonCriticalIds = items.filter((i) => i.is_included && !cpResult.criticalItemIds.has(i.id)).map((i) => i.id);
      if (criticalIds.length > 0) { await supabase.from('jphph_project_schedule_items').update({ is_critical: true, updated_at: new Date().toISOString() }).in('id', criticalIds); }
      if (nonCriticalIds.length > 0) { await supabase.from('jphph_project_schedule_items').update({ is_critical: false, updated_at: new Date().toISOString() }).in('id', nonCriticalIds); }
      setCriticalPathEnabled(true);
      setShowRecalcConfirm(false);
      setSaveSuccess('Critical path recalculated and saved');
      reloadItems();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to recalculate critical path');
    } finally {
      setSaving(false);
    }
  }, [projectId, items, links, effectiveSettings, allHolidays, reloadItems]);

  const handleResetCriticalPathFlags = useCallback(async () => {
    if (!projectId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await supabase.from('jphph_project_schedule_items').update({ is_critical: false, schedule_risk_level: 'normal', delay_status: 'on_track', updated_at: new Date().toISOString() }).eq('project_id', projectId);
      setCriticalPathEnabled(false);
      setShowCriticalPathOnly(false);
      setSaveSuccess('Critical path flags reset for all items');
      reloadItems();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reset critical path flags');
    } finally {
      setSaving(false);
    }
  }, [projectId, reloadItems]);

  const handleGenerateTimeline = useCallback(async (startDate: string, mode: 'fill-empty' | 'overwrite-all', scope: 'all-active' | 'unfinished-only') => {
    if (!projectId || items.length === 0) { setSaveError('No schedule items to generate'); return; }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      let targetItems = items.filter((i) => i.is_included);
      if (scope === 'unfinished-only') targetItems = targetItems.filter((i) => !i.is_complete && i.progress < 100);
      if (mode === 'fill-empty') targetItems = targetItems.filter((i) => !i.start_date);
      if (targetItems.length === 0) { setSaveSuccess('No items need date generation'); setSaving(false); return; }
      targetItems.sort((a, b) => a.sort_order - b.sort_order);
      const now = new Date().toISOString();
      const dateMap = new Map<string, { start_date: string; end_date: string }>();
      let currentDate = startDate;
      for (const item of targetItems) {
        const hasPredecessors = links.some((l) => l.successor_item_id === item.id);
        const itemStart = hasPredecessors ? (item.start_date || currentDate) : currentDate;
        const itemEnd = calculateEndDate(itemStart, item.duration_days || 1, effectiveSettings, allHolidays);
        dateMap.set(item.id, { start_date: itemStart, end_date: itemEnd });
        currentDate = nextWorkingDay(itemEnd, effectiveSettings, allHolidays);
      }
      const { updatedItems } = recalculateSchedule(items.map((i) => ({ ...i, start_date: dateMap.get(i.id)?.start_date ?? i.start_date, end_date: dateMap.get(i.id)?.end_date ?? i.end_date })), links, effectiveSettings, allHolidays);
      for (const [id, dates] of updatedItems) dateMap.set(id, dates);
      const updates = Array.from(dateMap.entries()).map(([id, dates]) => ({ id, start_date: dates.start_date, end_date: dates.end_date, updated_at: now }));
      if (updates.length === 0) { setSaveSuccess('No updates needed'); setSaving(false); return; }
      const CHUNK = 50;
      for (let i = 0; i < updates.length; i += CHUNK) {
        const chunk = updates.slice(i, i + CHUNK);
        await Promise.all(chunk.map((u) => supabase.from('jphph_project_schedule_items').update({ start_date: u.start_date, end_date: u.end_date, updated_at: u.updated_at }).eq('id', u.id)));
      }
      _scheduleCache.delete(projectId);
      reloadItems();
      setSaveSuccess(`Generated timeline for ${updates.length} item${updates.length > 1 ? 's' : ''}`);
      try { await persistProjectSummary(projectId); } catch (persistErr) { console.error('Failed to persist project summary after generate timeline:', persistErr); }
      try { window.dispatchEvent(new CustomEvent('__jphph_schedule_sync__', { detail: { projectId, action: 'generate_timeline', timestamp: now } })); } catch { /* noop */ }
    } catch (err: unknown) {
      console.error('Generate timeline error:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to generate timeline');
    } finally {
      setSaving(false);
    }
  }, [projectId, items, links, effectiveSettings, allHolidays, reloadItems]);

  const handleGanttEditSave = useCallback(async (recalcDownstream: boolean) => {
    if (!projectId || !ganttEdit) return;

    const { item, newStart, newEnd, newDuration } = ganttEdit;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const now = new Date().toISOString();
      const updatePayload = {
        start_date: newStart,
        end_date: newEnd,
        duration_days: Math.max(1, newDuration),
        updated_at: now,
      };

      const { data, error } = await supabase
        .from('jphph_project_schedule_items')
        .update(updatePayload)
        .eq('id', item.id)
        .eq('is_deleted', false)
        .select()
        .maybeSingle();

      if (error) {
        setSaveError(`Failed to save: ${error.message}`);
        return;
      }

      if (!data) {
        setSaveError('Update failed: item not found or access denied. Please refresh and try again.');
        return;
      }

      const updatedItem: ScheduleItem = {
        ...item,
        ...updatePayload,
      };

      setItems((prev) => prev.map((i) => (i.id === item.id ? updatedItem : i)));

      if (recalcDownstream && hasSuccessors(item.id)) {
        const allItemsForRecalc = items.map((i) => (i.id === item.id ? updatedItem : i));
        const sourceDeltaDays = item.start_date && newStart
          ? scheduleCalendarDayDelta(item.start_date, newStart)
          : 0;

        const undoMap = new Map<string, { start_date: string; end_date: string; duration_days?: number }>();
        if (item.start_date && item.end_date) {
          undoMap.set(item.id, {
            start_date: item.start_date,
            end_date: item.end_date,
            duration_days: item.duration_days,
          });
        }
        for (const i of items) {
          if (i.id === item.id || !i.start_date || !i.end_date) continue;
          undoMap.set(i.id, { start_date: i.start_date, end_date: i.end_date });
        }

        const recalcResult = recalculateSchedule(
          allItemsForRecalc,
          links,
          effectiveSettings,
          allHolidays,
          {
            triggerItemIds: [item.id],
            fixedItemIds: [item.id],
            sourceDeltaDays,
            skipCompletedItems: true,
            projectId,
            triggerSource: 'ganttDrag',
          }
        );

        if (undoMap.size > 0) {
          setScheduleUndoSnapshot(undoMap);
        }

        await applyDownstreamRecalculation(recalcResult, {
          excludeItemId: item.id,
          now,
          successPrefix: 'Schedule updated.',
        });
      } else {
        setScheduleUndoSnapshot(null);
        setSaveSuccess('Schedule updated');
      }

      _scheduleCache.delete(projectId);
      setGanttEdit(null);

      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        console.error('Failed to persist project summary after gantt edit:', persistErr);
      }

      try {
        window.dispatchEvent(new CustomEvent('__jphph_schedule_sync__', {
          detail: { projectId, action: 'gantt_edit', timestamp: now },
        }));
      } catch {
        /* noop */
      }
    } catch (err: unknown) {
      console.error('handleGanttEditSave error:', err);
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [
    projectId,
    ganttEdit,
    items,
    links,
    effectiveSettings,
    allHolidays,
    hasSuccessors,
    applyDownstreamRecalculation,
    setItems,
  ]);

  const handleAddLink = useCallback(async (
    predecessorId: string,
    linkType: 'FS' | 'SS',
    lagDays: number
  ) => {
    if (!editItem) return;
    await handleUpsertLink(null, {
      predecessor_item_id: predecessorId,
      successor_item_id: editItem.id,
      link_type: linkType,
      lag_days: lagDays,
    });
  }, [editItem, handleUpsertLink]);

  const handleScheduleUndo = useCallback(async () => {
    if (!projectId || !scheduleUndoSnapshot || scheduleUndoSnapshot.size === 0) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    const now = new Date().toISOString();

    try {
      const updates = Array.from(scheduleUndoSnapshot.entries()).map(([id, dates]) => ({
        id,
        start_date: dates.start_date,
        end_date: dates.end_date,
        duration_days: dates.duration_days,
        updated_at: now,
      }));

      const CHUNK = 50;
      const errors: string[] = [];
      for (let i = 0; i < updates.length; i += CHUNK) {
        const chunk = updates.slice(i, i + CHUNK);
        const results = await Promise.all(
          chunk.map((u) => {
            const payload: {
              start_date: string;
              end_date: string;
              updated_at: string;
              duration_days?: number;
            } = {
              start_date: u.start_date,
              end_date: u.end_date,
              updated_at: u.updated_at,
            };
            if (u.duration_days != null) {
              payload.duration_days = u.duration_days;
            }
            return supabase
              .from('jphph_project_schedule_items')
              .update(payload)
              .eq('id', u.id);
          })
        );
        results.forEach((r, idx) => {
          if (r.error) {
            errors.push(`${chunk[idx]?.id}: ${r.error.message}`);
          }
        });
      }

      if (errors.length > 0) {
        console.error('[handleScheduleUndo] update errors:', errors);
        setSaveError(
          errors.length === updates.length
            ? `Undo failed: ${errors[0]}`
            : `Undo partially failed (${errors.length}/${updates.length} items): ${errors[0]}`
        );
        return;
      }

      const undoMap = scheduleUndoSnapshot;
      setItems((prev) =>
        prev.map((i) => {
          const upd = undoMap.get(i.id);
          if (!upd) return i;
          return {
            ...i,
            start_date: upd.start_date,
            end_date: upd.end_date,
            ...(upd.duration_days != null ? { duration_days: upd.duration_days } : {}),
          };
        })
      );

      _scheduleCache.delete(projectId);
      setScheduleUndoSnapshot(null);
      setSaveSuccess('Schedule changes undone');

      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        console.error('Failed to persist project summary after undo:', persistErr);
      }

      try {
        window.dispatchEvent(new CustomEvent('__jphph_schedule_sync__', {
          detail: { projectId, action: 'schedule_undo', timestamp: now },
        }));
      } catch {
        /* noop */
      }
    } catch (err: unknown) {
      console.error('handleScheduleUndo error:', err);
      setSaveError(err instanceof Error ? err.message : 'Undo failed');
    } finally {
      setSaving(false);
    }
  }, [projectId, scheduleUndoSnapshot, setItems]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const templateInfo = null; // placeholder for template detection logic
  const defaultSettings = effectiveSettings;

  const filteredItems = useMemo(() => {
    // Gantt/list base set: show all rows when every item was excluded (legacy DB state).
    const allExplicitlyExcluded =
      items.length > 0 && items.every((i) => i.is_included === false);
    let result =
      showExcluded || allExplicitlyExcluded ? items : includedItems;

    if (filterTitleSearch.trim()) {
      const q = filterTitleSearch.toLowerCase();
      result = result.filter((i) =>
        i.title.toLowerCase().includes(q) ||
        (i.description || '').toLowerCase().includes(q)
      );
    }

    if (filterAssignedTo !== 'all') {
      result = result.filter((i) => i.assigned_to === filterAssignedTo);
    }

    if (filterStatus !== 'all') {
      result = result.filter((i) => i.status === filterStatus);
    }

    if (filterPhase !== 'all') {
      result = result.filter((i) => i.phase === filterPhase);
    }

    if (completionFilter !== 'all') {
      result = result.filter((i) =>
        completionFilter === 'complete' ? i.is_complete : !i.is_complete
      );
    }

    if (mainStageFilter !== 'all') {
      result = result.filter((i) => i.main_stage === mainStageFilter);
    }

    if (constructionSubStageFilter !== 'all') {
      result = result.filter(
        (i) => i.construction_sub_stage === constructionSubStageFilter
      );
    }

    if (showCriticalPathOnly && criticalPathEnabled) {
      result = result.filter((i) => criticalItemIds.has(i.id));
    }

    if (filterDateRange !== 'all' && filterDateRange !== 'custom') {
      const now = new Date().toISOString().split('T')[0];
      if (filterDateRange === 'this-week') {
        result = result.filter((i) => {
          const sd = i.start_date || '';
          return sd >= now && sd <= now; // simplified
        });
      } else if (filterDateRange === 'this-month') {
        result = result.filter((i) => {
          const sd = i.start_date || '';
          return sd.startsWith(now.slice(0, 7));
        });
      } else if (filterDateRange === 'overdue') {
        result = result.filter((i) => {
          const ed = i.end_date || '';
          return ed && ed < now && !i.is_complete;
        });
      } else if (filterDateRange === 'upcoming') {
        result = result.filter((i) => {
          const sd = i.start_date || '';
          return sd && sd >= now && !i.is_complete;
        });
      }
    }

    if (filterDateRange === 'custom' && (filterCustomStart || filterCustomEnd)) {
      result = result.filter((i) => {
        const sd = i.start_date || '';
        const ed = i.end_date || '';
        if (filterCustomStart && sd && sd < filterCustomStart) return false;
        if (filterCustomEnd && ed && ed > filterCustomEnd) return false;
        return true;
      });
    }

    // ── NEW: Apply pendingFilter to result ──
    // CRITICAL: Use unified completion logic matching isScheduleItemFinalComplete.
    // An item is "complete" if: is_complete=true OR progress>=100 OR actual_end_date set
    //                            OR all linked checklist items are approved.
    // "No checklist mapped" is a mapping status, NOT a completion status.
    if (pendingFilter !== 'all') {
      result = result.filter((item) => {
        const cp = checklistProgressMap.get(item.id);
        const hasLinked = cp?.hasLinkedItems ?? false;
        // Unified completion: same logic as isScheduleItemFinalComplete
        const isItemComplete =
          item.is_complete === true ||
          item.progress >= 100 ||
          (item.actual_end_date != null && item.actual_end_date !== '') ||
          (hasLinked && cp?.allRequiredComplete === true);
        switch (pendingFilter) {
          case 'pending_final':
            return !isItemComplete;
          case 'final_100':
            return isItemComplete;
          case 'no_mapping':
            return !hasLinked;
          case 'incomplete_checklist':
            return hasLinked && !cp?.allRequiredComplete;
          default:
            return true;
        }
      });
    }

    return result;
  }, [
    items,
    includedItems,
    showExcluded,
    filterTitleSearch,
    filterAssignedTo,
    filterStatus,
    filterPhase,
    completionFilter,
    mainStageFilter,
    constructionSubStageFilter,
    showCriticalPathOnly,
    filterDateRange,
    filterCustomStart,
    filterCustomEnd,
    criticalItemIds,
    checklistProgressMap,
    pendingFilter,
  ]);

  const clearAllFilters = useCallback(() => {
    setPendingFilter('all');
    setFilterTitleSearch('');
    setFilterAssignedTo('all');
    setFilterStatus('all');
    setFilterPhase('all');
    setFilterDateRange('all');
    setFilterCustomStart('');
    setFilterCustomEnd('');
    setShowExcluded(true);
    setCompletionFilter('all');
    setShowCriticalPathOnly(false);
    setMainStageFilter('all');
    setConstructionSubStageFilter('all');
  }, [setPendingFilter]);

  const hasActiveFilters =
    pendingFilter !== 'all' ||
    completionFilter !== 'all' ||
    mainStageFilter !== 'all' ||
    constructionSubStageFilter !== 'all' ||
    showCriticalPathOnly ||
    !showExcluded ||
    filterTitleSearch.trim() !== '' ||
    filterAssignedTo !== 'all' ||
    filterStatus !== 'all' ||
    filterPhase !== 'all' ||
    filterDateRange !== 'all';

  const filtersHideAllRows = items.length > 0 && filteredItems.length === 0 && hasActiveFilters;
  const allExplicitlyExcluded =
    items.length > 0 && items.every((i) => i.is_included === false);
  const trulyAllExcluded = allExplicitlyExcluded && includedItems.length === 0;

  useEffect(() => {
    if (itemsLoading || !projectId) return;
    const excluded = items.filter((i) => i.is_included === false).length;
    console.log('[useScheduleTab] schedule filter stats', {
      projectId,
      total: items.length,
      included: includedItems.length,
      excluded,
      filtered: filteredItems.length,
      pendingFilter,
      showExcluded,
      trulyAllExcluded,
    });
  }, [
    projectId,
    items,
    itemsLoading,
    includedItems.length,
    filteredItems.length,
    pendingFilter,
    showExcluded,
    trulyAllExcluded,
  ]);

  useEffect(() => {
    if (itemsLoading || items.length === 0) return;
    if (trulyAllExcluded && !showExcluded) {
      setShowExcluded(true);
    }
  }, [itemsLoading, items.length, trulyAllExcluded, showExcluded]);

  const initialFilterGuardRef = useRef<string | null>(null);
  useEffect(() => {
    if (itemsLoading || items.length === 0 || !projectId) return;
    if (initialFilterGuardRef.current === projectId) return;
    initialFilterGuardRef.current = projectId;

    if (filteredItems.length > 0 || !hasActiveFilters) return;

    console.warn('[useScheduleTab] resetting restrictive schedule filters that hide all items', {
      projectId,
      itemCount: items.length,
      pendingFilter,
    });
    clearAllFilters();
  }, [
    projectId,
    itemsLoading,
    items.length,
    filteredItems.length,
    hasActiveFilters,
    pendingFilter,
    clearAllFilters,
  ]);

  // NEW: Bulk mark selected items as complete
  const handleBulkMarkComplete = useCallback(async (itemIds: string[]) => {
    if (!projectId || itemIds.length === 0) return;
    setBulkMarking(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const today = now.split('T')[0];
      // Update all selected items to complete in parallel
      const updateResults = await Promise.all(
        itemIds.map((id) =>
          supabase
            .from('jphph_project_schedule_items')
            .update({
              is_complete: true,
              progress: 100,
              actual_end_date: today,
              updated_at: now,
            })
            .eq('id', id)
        )
      );

      const errors = updateResults
        .map((r, i) => (r.error ? { id: itemIds[i], error: r.error.message } : null))
        .filter(Boolean);
      if (errors.length > 0) {
        // eslint-disable-next-line no-console
        jphDebugLog('Bulk mark complete errors', errors);
        setSaveError(`${errors.length} of ${itemIds.length} items failed to update`);
        return;
      }

      // eslint-disable-next-line no-console
      jphDebugLog('Bulk mark complete success', { itemIds, count: itemIds.length });

      // Optimistic local update
      setItems((prev) =>
        prev.map((i) =>
          itemIds.includes(i.id)
            ? { ...i, is_complete: true, progress: 100, actual_end_date: today, updated_at: now }
            : i
        )
      );

      // Invalidate caches
      // cache removed — no-op
      _scheduleCache.delete(projectId);

      // CRITICAL: Persist project summary to jphph_projects table
      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        // eslint-disable-next-line no-console
        console.error('Failed to persist project summary after bulk mark:', persistErr);
      }

      // Broadcast sync
      try {
        window.dispatchEvent(
          new CustomEvent('__jphph_schedule_sync__', {
            detail: { projectId, bulkItemIds: itemIds, isComplete: true, timestamp: now },
          })
        );
      } catch { /* noop */ }

      await reloadStages();
      setSaveSuccess(`Marked ${itemIds.length} item${itemIds.length > 1 ? 's' : ''} as complete`);
      setSelectedItemIds(new Set());
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      jphDebugLog('Bulk mark complete error', { error: err instanceof Error ? err.message : 'Unknown error' });
      setSaveError(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkMarking(false);
    }
  }, [projectId, reloadStages]);

  // NEW: Mark ALL pending schedule items as complete (no selection required)
  const handleMarkAllPendingComplete = useCallback(async (): Promise<number> => {
    if (!projectId) return 0;
    const pending = items.filter((i) => i.is_included !== false && !i.is_complete && i.progress < 100);
    if (pending.length === 0) return 0;
    setBulkMarking(true);
    setSaveError(null);
    try {
      const now = new Date().toISOString();
      const today = now.split('T')[0];
      const itemIds = pending.map((i) => i.id);

      const updateResults = await Promise.all(
        itemIds.map((id) =>
          supabase
            .from('jphph_project_schedule_items')
            .update({
              is_complete: true,
              progress: 100,
              actual_end_date: today,
              updated_at: now,
            })
            .eq('id', id)
        )
      );

      const errors = updateResults
        .map((r, i) => (r.error ? { id: itemIds[i], error: r.error.message } : null))
        .filter(Boolean);
      if (errors.length > 0) {
        // eslint-disable-next-line no-console
        jphDebugLog('Mark all pending errors', errors);
        setSaveError(`${errors.length} of ${itemIds.length} items failed to update`);
        return 0;
      }

      // eslint-disable-next-line no-console
      jphDebugLog('Mark all pending complete success', { count: itemIds.length, itemIds });

      // Optimistic local update
      setItems((prev) =>
        prev.map((i) =>
          itemIds.includes(i.id)
            ? { ...i, is_complete: true, progress: 100, actual_end_date: today, updated_at: now }
            : i
        )
      );

      // Invalidate caches
      // cache removed — no-op
      _scheduleCache.delete(projectId);

      // CRITICAL: Persist project summary to jphph_projects table
      try {
        await persistProjectSummary(projectId);
      } catch (persistErr) {
        // eslint-disable-next-line no-console
        console.error('Failed to persist project summary after mark all pending:', persistErr);
      }

      // Broadcast sync
      try {
        window.dispatchEvent(
          new CustomEvent('__jphph_schedule_sync__', {
            detail: { projectId, bulkItemIds: itemIds, isComplete: true, timestamp: now },
          })
        );
      } catch { /* noop */ }

      await reloadStages();
      setSaveSuccess(`Marked ${itemIds.length} pending item${itemIds.length > 1 ? 's' : ''} as complete`);
      setSelectedItemIds(new Set());
      return itemIds.length;
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      jphDebugLog('Mark all pending error', { error: err instanceof Error ? err.message : 'Unknown error' });
      setSaveError(err instanceof Error ? err.message : 'Bulk update failed');
      return 0;
    } finally {
      setBulkMarking(false);
    }
  }, [projectId, items, reloadStages]);

  // NEW: Toggle item selection for bulk actions
  const toggleSelectItem = useCallback((itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedItemIds(new Set(filteredItems.map((i) => i.id)));
  }, [filteredItems]);

  const clearSelection = useCallback(() => {
    setSelectedItemIds(new Set());
  }, []);

  return {
    // Core data
    items,
    links,
    settings,
    holidays,
    projectHolidays,
    allHolidays,
    criticalItemIds,
    computedRisk,
    healthSummary,
    includedItems,
    filteredItems,
    loading,
    itemsLoading,
    error,
    settingsLoading,

    clearAllFilters,
    hasActiveFilters,
    filtersHideAllRows,
    trulyAllExcluded,

    // UI state
    viewMode,
    setViewMode,
    completionFilter,
    setCompletionFilter,
    showExcluded,
    setShowExcluded,
    isFullscreen,
    setIsFullscreen,
    showDebug,
    setShowDebug,
    showChangeLog,
    setShowChangeLog,
    showAllLinks,
    setShowAllLinks,

    // Scale & grouping
    ganttScale,
    setGanttScale,
    showPhases,
    setShowPhases,
    criticalPathEnabled,
    setCriticalPathEnabled,
    showCriticalPathOnly,
    setShowCriticalPathOnly,

    // Recalc confirm
    showRecalcConfirm,
    setShowRecalcConfirm,

    // Setup mode
    isSetupMode,

    // Drawer state
    filterPanelOpen,
    setFilterPanelOpen,
    historyPanelOpen,
    setHistoryPanelOpen,
    exportPanelOpen,
    setExportPanelOpen,

    // Filter fields
    filterTitleSearch,
    setFilterTitleSearch,
    filterAssignedTo,
    setFilterAssignedTo,
    filterStatus,
    setFilterStatus,
    filterPhase,
    setFilterPhase,
    filterDateRange,
    setFilterDateRange,
    filterCustomStart,
    setFilterCustomStart,
    filterCustomEnd,
    setFilterCustomEnd,

    // Modal state
    showItemModal,
    setShowItemModal,
    editItem,
    setEditItem,
    ganttEdit,
    setGanttEdit,
    scheduleUndoSnapshot,
    handleScheduleUndo,
    createDefaults,
    clearCreateDefaults,
    openCreateItem,
    openCreateItemAtDate,
    showGenerateModal,
    setShowGenerateModal,
    showBaselineModal,
    setShowBaselineModal,
    showLinkModal,
    setShowLinkModal,
    showSettingsModal,
    setShowSettingsModal,

    // Save / error handling
    saving,
    savingLink,
    deletingLinkId,
    editingLinkId,
    saveError,
    saveSuccess,
    notificationWarning,
    clearMessages,
    setSettingsForm,
    savingProjHoliday,
    setSavingProjHoliday,

    // Actions
    reload,
    reloadSettings,
    reloadHolidays,
    handleIncludeAll,
    handleExcludeAll,
    handleToggleIncluded,
    handleSaveItem,
    handleGanttEditSave,
    handleGanttBarChange,
    handleDelete,
    handleUpsertLink,
    handleRemoveLink,
    handleAddLink,
    handleRecalc,
    handleRecalcCriticalPath,
    handleRecalculateProgress,
    confirmRecalcCriticalPath,
    handleResetCriticalPathFlags,
    handleGenerateTimeline,
    handleSaveBaseline,
    handleSaveSettings,
    handleAddProjectHoliday,
    handleRemoveProjectHoliday,
    handleToggleComplete,
    handleProgressChange,
    handleSetupSchedule,
    scheduleSetupState,
    scheduleSetupMessage,
    toggleFullscreen,
    hasSuccessors,
    templateInfo,
    defaultSettings,
    mainStageFilter,
    setMainStageFilter,
    constructionSubStageFilter,
    setConstructionSubStageFilter,

    // Checklist progress integration
    checklistProgressMap,
    linkedChecklists,
    reloadChecklistProgress,
    completionBlockItem,
    setCompletionBlockItem,
    onOpenCompletionBlock,

    // Auth context
    effectiveRole,
    isManagement,
    canEditReal,
    user,
    profile,
    projectId,
    projectType,

    // NEW pending filter
    pendingFilter,
    setPendingFilter,

    // NEW bulk selection
    selectedItemIds,
    toggleSelectItem,
    selectAllVisible,
    clearSelection,
    handleBulkMarkComplete,
    handleMarkAllPendingComplete,
    bulkMarking,
  };
}
